import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Don't set Content-Type for FormData - let axios handle it
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userRole');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Auth API calls
export const authAPI = {
    login: (credentials, role) => {
        const endpoints = {
            admin: '/admin/login',
            employee: '/employee/login',
            client: '/client/login',
            vendor: '/vendor/login',
        };
        return api.post(endpoints[role], credentials);
    },
    logout: () => {
        return api.post('/logout');
    },
    getUser: () => {
        return api.get('/user');
    },
};

// HRM API calls
export const hrmAPI = {
    // Departments
    getDepartments: (page = 1) => api.get(`/departments?page=${page}`),
    getDepartment: (id) => api.get(`/departments/${id}`),
    createDepartment: (data) => api.post('/departments', data),
    updateDepartment: (id, data) => api.put(`/departments/${id}`, data),
    deleteDepartment: (id) => api.delete(`/departments/${id}`),

    // Designations
    getDesignations: (page = 1) => api.get(`/designations?page=${page}`),
    getDesignation: (id) => api.get(`/designations/${id}`),
    createDesignation: (data) => api.post('/designations', data),
    updateDesignation: (id, data) => api.put(`/designations/${id}`, data),
    deleteDesignation: (id) => api.delete(`/designations/${id}`),

    // Employees
    getEmployees: (page = 1) => api.get(`/employees?page=${page}`),
    getEmployee: (id) => api.get(`/employees/${id}`),
    createEmployee: (data) => api.post('/employees', data),
    updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
    deleteEmployee: (id) => api.delete(`/employees/${id}`),

    // Branches
    getBranches: (page = 1) => api.get(`/branches?page=${page}`),
    createBranch: (data) => api.post('/branches', data),
    updateBranch: (id, data) => api.put(`/branches/${id}`, data),
    deleteBranch: (id) => api.delete(`/branches/${id}`),

    // Employee Categories
    getEmployeeCategories: (page = 1) => api.get(`/employee-categories?page=${page}`),
    createEmployeeCategory: (data) => api.post('/employee-categories', data),
    updateEmployeeCategory: (id, data) => api.put(`/employee-categories/${id}`, data),
    deleteEmployeeCategory: (id) => api.delete(`/employee-categories/${id}`),

    // Attendances
    getAttendances: () => api.get('/attendances'),
    createAttendance: (data) => api.post('/attendances', data),
    updateAttendance: (id, data) => api.put(`/attendances/${id}`, data),
    deleteAttendance: (id) => api.delete(`/attendances/${id}`),
    
    // Leave Types
    getLeaveTypes: (page = 1) => api.get(`/leave-types?page=${page}`),
    createLeaveType: (data) => api.post('/leave-types', data),
    updateLeaveType: (id, data) => api.put(`/leave-types/${id}`, data),
    deleteLeaveType: (id) => api.delete(`/leave-types/${id}`),
    updateLeaveType: (id, data) => api.put(`/leave-types/${id}`, data),
    deleteLeaveType: (id) => api.delete(`/leave-types/${id}`),

    // Leave Applications
    getLeaveApplications: (page = 1) => api.get(`/leave-applications?page=${page}`),
    createLeaveApplication: (data) => api.post('/leave-applications', data),
    updateLeaveApplication: (id, data) => api.put(`/leave-applications/${id}`, data),
    deleteLeaveApplication: (id) => api.delete(`/leave-applications/${id}`),

    // Salary Structures
    getSalaryStructures: (page = 1) => api.get(`/salary-structures?page=${page}`),
    createSalaryStructure: (data) => api.post('/salary-structures', data),
    updateSalaryStructure: (id, data) => api.put(`/salary-structures/${id}`, data),
    deleteSalaryStructure: (id) => api.delete(`/salary-structures/${id}`),

    // Pay Slips
    getPaySlips: () => api.get('/pay-slips'),
    createPaySlip: (data) => api.post('/pay-slips', data),

    // Loan Applications
    getLoanApplications: (page = 1) => api.get(`/loan-applications?page=${page}`),
    createLoanApplication: (data) => api.post('/loan-applications', data),
    updateLoanApplication: (id, data) => api.put(`/loan-applications/${id}`, data),
    deleteLoanApplication: (id) => api.delete(`/loan-applications/${id}`),

    // Leave Policies
    getLeavePolicies: (page = 1) => api.get(`/leave-policies?page=${page}`),
    createLeavePolicy: (data) => api.post('/leave-policies', data),
    updateLeavePolicy: (id, data) => api.put(`/leave-policies/${id}`, data),
    deleteLeavePolicy: (id) => api.delete(`/leave-policies/${id}`),

    // Movements
    getMovements: (page = 1) => api.get(`/movements?page=${page}`),
    createMovement: (data) => api.post('/movements', data),
    updateMovement: (id, data) => api.put(`/movements/${id}`, data),
    deleteMovement: (id) => api.delete(`/movements/${id}`),

    // Holidays
    getHolidays: (page = 1) => api.get(`/holidays?page=${page}`),
    createHoliday: (data) => api.post('/holidays', data),
    updateHoliday: (id, data) => api.put(`/holidays/${id}`, data),
    deleteHoliday: (id) => api.delete(`/holidays/${id}`),

    // Settings
    getSettings: () => api.get('/settings'),
    saveSetting: (key, value, type = 'time') => api.post('/settings', { key, value, type }),
    saveSettings: (settings) => api.post('/settings/bulk', { settings }),

    // Employee Documents
    getEmployeeDocuments: (page = 1) => api.get(`/employee-documents?page=${page}`),
    createEmployeeDocument: (data) => api.post('/employee-documents', data),
    updateEmployeeDocument: (id, data) => api.put(`/employee-documents/${id}`, data),
    deleteEmployeeDocument: (id) => api.delete(`/employee-documents/${id}`),

    // Employee Emergency Contacts
    getEmployeeEmergencyContacts: (page = 1) => api.get(`/employee-emergency-contacts?page=${page}`),
    createEmployeeEmergencyContact: (data) => api.post('/employee-emergency-contacts', data),
    updateEmployeeEmergencyContact: (id, data) => api.put(`/employee-emergency-contacts/${id}`, data),
    deleteEmployeeEmergencyContact: (id) => api.delete(`/employee-emergency-contacts/${id}`),

    // Employee Experiences
    getEmployeeExperiences: (page = 1) => api.get(`/employee-experiences?page=${page}`),
    createEmployeeExperience: (data) => api.post('/employee-experiences', data),
    updateEmployeeExperience: (id, data) => api.put(`/employee-experiences/${id}`, data),
    deleteEmployeeExperience: (id) => api.delete(`/employee-experiences/${id}`),

    // Employee Qualifications
    getEmployeeQualifications: (page = 1) => api.get(`/employee-qualifications?page=${page}`),
    createEmployeeQualification: (data) => api.post('/employee-qualifications', data),
    updateEmployeeQualification: (id, data) => api.put(`/employee-qualifications/${id}`, data),
    deleteEmployeeQualification: (id) => api.delete(`/employee-qualifications/${id}`),
};

// Finance API calls
export const financeAPI = {
    // Chart of Accounts
    getChartOfAccounts: (page = 1, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/chart-of-accounts?page=${page}&${queryString}`);
    },
    getChartOfAccount: (id) => api.get(`/chart-of-accounts/${id}`),
    createChartOfAccount: (data) => api.post('/chart-of-accounts', data),
    updateChartOfAccount: (id, data) => api.put(`/chart-of-accounts/${id}`, data),
    deleteChartOfAccount: (id) => api.delete(`/chart-of-accounts/${id}`),
    getChartOfAccountsByType: () => api.get('/chart-of-accounts/by-type'),
    getChartAccountParents: (level) => api.get(`/chart-of-accounts/parents?level=${level}`),

    // Bank Accounts
    getBankAccounts: (page = 1) => api.get(`/bank-accounts?page=${page}`),
    getBankAccount: (id) => api.get(`/bank-accounts/${id}`),
    createBankAccount: (data) => api.post('/bank-accounts', data),
    updateBankAccount: (id, data) => api.put(`/bank-accounts/${id}`, data),
    deleteBankAccount: (id) => api.delete(`/bank-accounts/${id}`),

    // Opening Balance Settings
    getOpeningBalanceSettings: () => api.get('/finance-settings'),
    getContraAccounts: (type) => api.get(`/finance-settings/contra-accounts?type=${type}`),
    saveOpeningBalanceSetting: (data) => api.post('/finance-settings', data),
    deleteOpeningBalanceSetting: (accountType) => api.delete(`/finance-settings?account_type=${accountType}`),

    // Journal Entries
    getJournalEntries: (page = 1) => api.get(`/journal-entries?page=${page}`),
    getJournalEntry: (id) => api.get(`/journal-entries/${id}`),
    createJournalEntry: (data) => api.post('/journal-entries', data),
    updateJournalEntry: (id, data) => api.put(`/journal-entries/${id}`, data),
    deleteJournalEntry: (id) => api.delete(`/journal-entries/${id}`),
};

// Inventory API calls
export const inventoryAPI = {
    // Stores
    getStores: (page = 1) => api.get(`/stores?page=${page}`),
    getStore: (id) => api.get(`/stores/${id}`),
    createStore: (data) => api.post('/stores', data),
    updateStore: (id, data) => api.put(`/stores/${id}`, data),
    deleteStore: (id) => api.delete(`/stores/${id}`),

    // Product Categories
    getProductCategories: (page = 1) => api.get(`/product-categories?page=${page}`),
    getProductCategory: (id) => api.get(`/product-categories/${id}`),
    createProductCategory: (data) => api.post('/product-categories', data),
    updateProductCategory: (id, data) => api.put(`/product-categories/${id}`, data),
    deleteProductCategory: (id) => api.delete(`/product-categories/${id}`),

    // Units
    getUnits: (page = 1) => api.get(`/units?page=${page}`),
    getUnit: (id) => api.get(`/units/${id}`),
    createUnit: (data) => api.post('/units', data),
    updateUnit: (id, data) => api.put(`/units/${id}`, data),
    deleteUnit: (id) => api.delete(`/units/${id}`),

    // Products
    getProducts: (page = 1) => api.get(`/products?page=${page}`),
    getProduct: (id) => api.get(`/products/${id}`),
    createProduct: (data) => api.post('/products', data),
    updateProduct: (id, data) => api.put(`/products/${id}`, data),
    deleteProduct: (id) => api.delete(`/products/${id}`),

    // Inventory Stocks
    getInventoryStocks: (page = 1) => api.get(`/inventory-stocks?page=${page}`),
    getInventoryStock: (id) => api.get(`/inventory-stocks/${id}`),
    createInventoryStock: (data) => api.post('/inventory-stocks', data),
    updateInventoryStock: (id, data) => api.put(`/inventory-stocks/${id}`, data),
    deleteInventoryStock: (id) => api.delete(`/inventory-stocks/${id}`),

    // Inventory Movements
    getInventoryMovements: (page = 1) => api.get(`/inventory-movements?page=${page}`),
    createInventoryMovement: (data) => api.post('/inventory-movements', data),
};

// CRM API calls
export const crmAPI = {
    // Clients
    getClients: (page = 1) => api.get(`/clients?page=${page}`),
    getClient: (id) => api.get(`/clients/${id}`),
    createClient: (data) => api.post('/clients', data),
    updateClient: (id, data) => api.put(`/clients/${id}`, data),
    deleteClient: (id) => api.delete(`/clients/${id}`),

    // Projects
    getProjects: (page = 1) => api.get(`/projects?page=${page}`),
    getProject: (id) => api.get(`/projects/${id}`),
    createProject: (data) => api.post('/projects', data),
    updateProject: (id, data) => api.put(`/projects/${id}`, data),
    deleteProject: (id) => api.delete(`/projects/${id}`),

    // Lifts
    getLifts: (page = 1) => api.get(`/lifts?page=${page}`),
    getLift: (id) => api.get(`/lifts/${id}`),
    createLift: (data) => api.post('/lifts', data),
    updateLift: (id, data) => api.put(`/lifts/${id}`, data),
    deleteLift: (id) => api.delete(`/lifts/${id}`),

    // Lift Brands
    getLiftBrands: (page = 1) => api.get(`/lift-brands?page=${page}`),
    createLiftBrand: (data) => api.post('/lift-brands', data),
    updateLiftBrand: (id, data) => api.put(`/lift-brands/${id}`, data),
    deleteLiftBrand: (id) => api.delete(`/lift-brands/${id}`),

    // Lift Types
    getLiftTypes: (page = 1) => api.get(`/lift-types?page=${page}`),
    createLiftType: (data) => api.post('/lift-types', data),
    updateLiftType: (id, data) => api.put(`/lift-types/${id}`, data),
    deleteLiftType: (id) => api.delete(`/lift-types/${id}`),

    // Invoices
    getInvoices: (page = 1, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/invoices?page=${page}&${queryString}`);
    },
    getInvoice: (id) => api.get(`/invoices/${id}`),
    createInvoice: (data) => api.post('/invoices', data),
    updateInvoice: (id, data) => api.put(`/invoices/${id}`, data),
    deleteInvoice: (id) => api.delete(`/invoices/${id}`),
    generateInvoiceFromLifts: (data) => api.post('/invoices/generate-from-lifts', data),
    generateMultiMonthInvoice: (data) => api.post('/invoices/generate-multi-month', data),
    mergeInvoices: (data) => api.post('/invoices/merge', data),
    getProjectLifts: (projectId) => api.get(`/invoices/project-lifts?project_id=${projectId}`),
    getLiftsForBilling: (params) => api.get('/invoices/lifts-for-billing', { params }),
};

// Vendor API calls
export const vendorAPI = {
    getVendors: (page = 1) => api.get(`/vendors?page=${page}`),
    getVendor: (id) => api.get(`/vendors/${id}`),
    createVendor: (data) => api.post('/vendors', data),
    updateVendor: (id, data) => api.put(`/vendors/${id}`, data),
    deleteVendor: (id) => api.delete(`/vendors/${id}`),
};

// Company Settings API calls
export const settingsAPI = {
    getCompanySettings: () => api.get('/company-settings'),
    saveCompanySettings: (data) => api.post('/company-settings', data),
    uploadLogo: (formData) => api.post('/company-settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteLogo: () => api.delete('/company-settings/logo'),
};

export default api;
